import type { MouseEvent, MouseEventHandler } from "react";
import { mergeProps } from "@base-ui/react/merge-props";
import { useRender } from "@base-ui/react/use-render";
import classNames from "classnames";
import styles from "./Link.module.scss";
import type { LinkProps } from "./Link.types";

export type { LinkForcedState, LinkProps, LinkSize } from "./Link.types";

const preventActivation = (event: MouseEvent<HTMLElement>) => {
    event.preventDefault();
};

/**
 * Padding-less text action or destination (Figma `Link`, node 4031:26).
 * Renders a `<button>` for actions, an `<a>` for `href`, or the element passed
 * to `render` (a router link) with Link's classes, ref and handlers merged in.
 */
const Link = ({
    size = "sm",
    disabled = false,
    iconOnly,
    leftIcon,
    rightIcon,
    render,
    className,
    children,
    ref,
    onClick,
    ...props
}: LinkProps) => {
    const isAnchor = render !== undefined || props.href !== undefined;
    const handleClick: MouseEventHandler<HTMLElement> | undefined = disabled
        ? preventActivation
        : onClick;

    const sharedProps = {
        className: classNames(
            styles.link,
            styles[size],
            { [styles.iconOnly]: iconOnly },
            className,
        ),
        onClick: handleClick,
        children: (
            <>
                {leftIcon == null ? null : (
                    <span className={styles.leftSlot}>{leftIcon}</span>
                )}
                {children}
                {rightIcon == null ? null : (
                    <span className={styles.rightSlot}>{rightIcon}</span>
                )}
            </>
        ),
    };

    // Dropping href and cancelling middle-click keeps a disabled anchor from
    // opening in a new tab, not only from the plain click.
    const disabledAnchorProps = disabled
        ? {
              "aria-disabled": true,
              tabIndex: -1,
              href: undefined,
              onAuxClick: preventActivation,
          }
        : {};

    const elementProps = isAnchor
        ? mergeProps<"a">(props, { ...sharedProps, ...disabledAnchorProps })
        : mergeProps<"button">(props, {
              ...sharedProps,
              type: props.type ?? "button",
              disabled,
          });

    return useRender({
        defaultTagName: isAnchor ? "a" : "button",
        render,
        ref,
        props: elementProps,
    });
};

export { Link };
